"use client";
import Link from "next/link";
import { social } from "@/data/data";
import { useRef, useEffect, useState } from "react";
import BlogCommentForm from "./blog-comment-form";
import { BlogPost, TableOfContentsItem } from "@/lib/blog-utils";
import CodeBlock from "@/components/ui/codeblock";

interface BlogSingleInfoProps {
    blog: BlogPost;
}

const BlogSingleInfo = ({ blog }: BlogSingleInfoProps) => {
    const scrollRef = useRef(null);

    // State for Get in Touch form
    const [getInTouchData, setGetInTouchData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmittingGetInTouch, setIsSubmittingGetInTouch] = useState(false);
    const [getInTouchStatus, setGetInTouchStatus] = useState<{
        type: 'success' | 'error' | null;
        message: string;
    }>({ type: null, message: '' });

    // State for active TOC item
    const [activeTocId, setActiveTocId] = useState<string>('');

    // State for TOC scroll position (removed unused variable)
    const tocRef = useRef<HTMLDivElement>(null);

    // Ref to track if we're in the middle of a programmatic scroll (from TOC click)
    const isProgrammaticScrollRef = useRef<boolean>(false);
    const programmaticScrollTargetRef = useRef<string>('');

    // Handle Get in Touch form input changes
    const handleGetInTouchInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setGetInTouchData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle Get in Touch form submission
    const handleGetInTouchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmittingGetInTouch(true);
        setGetInTouchStatus({ type: null, message: '' });

        try {
            const response = await fetch('/api/get-in-touch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...getInTouchData,
                    slug: `Blog - ${blog.slug}`
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setGetInTouchStatus({
                    type: 'success',
                    message: 'Message sent successfully! We will get back to you soon.'
                });
                setGetInTouchData({ name: '', email: '', message: '' });

                // Auto-hide success message after 3 seconds
                setTimeout(() => {
                    setGetInTouchStatus({ type: null, message: '' });
                }, 3000);
            } else {
                setGetInTouchStatus({
                    type: 'error',
                    message: data.error || 'Something went wrong. Please try again.'
                });

                // Auto-hide error message after 3 seconds
                setTimeout(() => {
                    setGetInTouchStatus({ type: null, message: '' });
                }, 3000);
            }
        } catch (error) {
            console.error('Error submitting get in touch form:', error);
            setGetInTouchStatus({
                type: 'error',
                message: 'Network error. Please check your connection and try again.'
            });

            // Auto-hide error message after 3 seconds
            setTimeout(() => {
                setGetInTouchStatus({ type: null, message: '' });
            }, 3000);
        } finally {
            setIsSubmittingGetInTouch(false);
        }
    };

    // Ensure TOC links work after component mounts
    useEffect(() => {
        // Wait for the content to be fully rendered
        const checkContent = () => {
            const contentElement = document.querySelector('.blog-content');
            if (contentElement && contentElement.children.length > 0) {
                // Content is loaded, TOC should work now
                return true;
            }
            return false;
        };

        if (checkContent()) {
            return;
        }

        // If content not ready, wait for it
        const timer = setTimeout(() => {
            checkContent();
        }, 500);

        return () => clearTimeout(timer);
    }, [blog.slug]);

    // Handle scroll events to update active TOC item and sync TOC scroll
    useEffect(() => {
        // Cache DOM elements and measurements to avoid repeated reads
        let cachedHeadings: HTMLElement[] = [];
        let cachedHeadingRects: DOMRect[] = [];
        let lastCacheTime = 0;
        const CACHE_DURATION = 100; // Cache for 100ms

        // Build a map of parent sections (level 2 with children) and their structure
        interface ParentSection { id: string; level: number; children: string[]; }
        const buildParentSectionMap = (): { parentMap: Map<string, string>; parentSections: ParentSection[] } => {
            if (!blog.tableOfContents || blog.tableOfContents.length === 0) {
                return { parentMap: new Map<string, string>(), parentSections: [] as ParentSection[] };
            }

            const parentMap = new Map<string, string>(); // childId -> parentId
            const parentSections: ParentSection[] = [];
            let currentParent: ParentSection | null = null;

            blog.tableOfContents.forEach((item) => {
                if (item.level === 2) {
                    // Save previous parent if exists
                    if (currentParent) {
                        parentSections.push(currentParent);
                    }
                    // Start new parent
                    currentParent = { id: item.id, level: item.level, children: [] };
                } else if (item.level === 3 && currentParent) {
                    // Add child to current parent
                    currentParent.children.push(item.id);
                    parentMap.set(item.id, currentParent.id);
                }
            });

            // Add last parent if exists
            if (currentParent) {
                parentSections.push(currentParent);
            }

            return { parentMap, parentSections };
        };

        const result = buildParentSectionMap();
        const parentMap = result.parentMap;
        const parentSections: ParentSection[] = result.parentSections;
        const updateHeadingCache = () => {
            const contentElement = document.querySelector('.blog-content');
            if (!contentElement) return false;

            const headings = contentElement.querySelectorAll('h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]');
            cachedHeadings = Array.from(headings) as HTMLElement[];

            // Batch all getBoundingClientRect calls to prevent forced reflows
            cachedHeadingRects = cachedHeadings.map(heading => heading.getBoundingClientRect());
            lastCacheTime = performance.now();

            return true;
        };

        const handleScroll = () => {
            if (!blog.tableOfContents || blog.tableOfContents.length === 0) return;

            const now = performance.now();

            // Update cache if needed (only when content changes or cache expires)
            if (cachedHeadings.length === 0 || (now - lastCacheTime) > CACHE_DURATION) {
                if (!updateHeadingCache()) return;
            }

            // Batch all DOM reads at once to prevent forced reflows
            const scrollY = window.scrollY;
            const innerHeight = window.innerHeight;
            const headerHeight = 150;
            const viewportTop = scrollY + headerHeight;
            const viewportBottom = scrollY + innerHeight;

            let currentHeading = '';
            let headingInViewport: HTMLElement | null = null;
            let bestMatch = { heading: null as HTMLElement | null, distance: Infinity };

            // First, find all headings in viewport
            const headingsInViewport: Array<{ element: HTMLElement; id: string; top: number; bottom: number }> = [];

            cachedHeadings.forEach((element, index) => {
                const rect = cachedHeadingRects[index];
                const elementTop = rect.top + scrollY;
                const elementBottom = elementTop + rect.height;

                // Check if heading is in viewport
                const isInViewport = elementTop <= viewportBottom && elementBottom >= viewportTop;

                if (isInViewport) {
                    headingsInViewport.push({ element, id: element.id, top: elementTop, bottom: elementBottom });

                    // Calculate how much of the heading is visible
                    const visibleTop = Math.max(elementTop, viewportTop);
                    const visibleBottom = Math.min(elementBottom, viewportBottom);
                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                    const visibilityRatio = visibleHeight / rect.height;

                    // Prefer headings that are more visible and closer to the top of viewport
                    const distanceFromTop = Math.abs(elementTop - viewportTop);
                    const score = visibilityRatio - (distanceFromTop / innerHeight) * 0.1;

                    if (score > 0.3 && distanceFromTop < bestMatch.distance) {
                        bestMatch = { heading: element, distance: distanceFromTop };
                        headingInViewport = element;
                    }
                }
            });

            // Determine which section we're currently in based on scroll position
            // A section is active if viewportTop is between the section's heading and the next section's heading
            let activeParentId: string | null = null;

            // First, check if we have parent sections
            if (parentSections.length > 0) {
                // Find which parent section we're currently in
                for (let i = 0; i < parentSections.length; i++) {
                    const parentSection = parentSections[i];
                    const parentElement = cachedHeadings.find(h => h.id === parentSection.id);

                    if (!parentElement) continue;

                    const parentIndex = cachedHeadings.indexOf(parentElement);
                    const parentRect = cachedHeadingRects[parentIndex];
                    const parentTop = parentRect.top + scrollY;

                    // Find the next parent section's position
                    let nextParentTop = Infinity;
                    if (i < parentSections.length - 1) {
                        const nextParentId = parentSections[i + 1].id;
                        const nextParentElement = cachedHeadings.find(h => h.id === nextParentId);
                        if (nextParentElement) {
                            const nextParentIndex = cachedHeadings.indexOf(nextParentElement);
                            const nextParentRect = cachedHeadingRects[nextParentIndex];
                            nextParentTop = nextParentRect.top + scrollY;
                        }
                    }

                    // Check if we're in this section's content area
                    // We're in this section if viewportTop is at or past this section's heading
                    // and before the next section's heading
                    if (viewportTop >= parentTop && viewportTop < nextParentTop) {
                        activeParentId = parentSection.id;
                        break;
                    }
                }
            }

            // If we found an active parent section, use it
            if (activeParentId) {
                currentHeading = activeParentId;
            } else {
                // Fallback: if we have headings in viewport, use the best match
                if (headingInViewport) {
                    currentHeading = (headingInViewport as HTMLElement).id;

                    // If it's a child, try to find its parent
                    const parentId = parentMap.get(currentHeading);
                    if (parentId) {
                        // Check if we're still in the parent's section
                        const parentSection = parentSections.find(p => p.id === parentId);
                        if (parentSection) {
                            const parentElement = cachedHeadings.find(h => h.id === parentId);
                            if (parentElement) {
                                const parentIndex = cachedHeadings.indexOf(parentElement);
                                const parentRect = cachedHeadingRects[parentIndex];
                                const parentTop = parentRect.top + scrollY;

                                const currentParentIndex = parentSections.indexOf(parentSection);
                                let nextParentTop = Infinity;

                                if (currentParentIndex >= 0 && currentParentIndex < parentSections.length - 1) {
                                    const nextParentId = parentSections[currentParentIndex + 1].id;
                                    const nextParentElement = cachedHeadings.find(h => h.id === nextParentId);
                                    if (nextParentElement) {
                                        const nextParentIndex = cachedHeadings.indexOf(nextParentElement);
                                        const nextParentRect = cachedHeadingRects[nextParentIndex];
                                        nextParentTop = nextParentRect.top + scrollY;
                                    }
                                }

                                // If we're still in the parent section's content area, use parent
                                if (viewportTop >= parentTop && viewportTop < nextParentTop) {
                                    currentHeading = parentId;
                                }
                            }
                        }
                    }
                } else {
                    // No heading in viewport - find the closest heading
                    cachedHeadings.forEach((element, index) => {
                        const rect = cachedHeadingRects[index];
                        const elementTop = rect.top + scrollY;
                        const distance = Math.abs(elementTop - viewportTop);

                        if (distance < bestMatch.distance) {
                            bestMatch = { heading: element, distance };
                            currentHeading = element.id;
                        }
                    });
                }
            }

            // Update active TOC item (this is a state update, not a DOM read)
            // But don't override if we're in the middle of a programmatic scroll
            if (currentHeading && currentHeading !== activeTocId) {
                // If we're programmatically scrolling to a target, don't override until we reach it
                if (isProgrammaticScrollRef.current && programmaticScrollTargetRef.current) {
                    // If the detected heading matches our target, we've reached it - allow update
                    if (currentHeading === programmaticScrollTargetRef.current) {
                        // We've reached the target, clear the flag
                        isProgrammaticScrollRef.current = false;
                        programmaticScrollTargetRef.current = '';
                    } else {
                        // Still scrolling to target, don't override the clicked item
                        return;
                    }
                }

                setActiveTocId(currentHeading);

                // Sync TOC scroll to show active item
                syncTocScroll(currentHeading);
            }
        };

        // Function to sync TOC scroll with active heading - Optimized to prevent forced reflows
        const syncTocScroll = (headingId: string) => {
            if (!tocRef.current) return;

            const tocContainer = tocRef.current;
            const activeTocLink = tocContainer.querySelector(`a[href="#${headingId}"]`);

            if (activeTocLink) {
                // Batch all DOM reads to prevent forced reflows
                const tocContainerRect = tocContainer.getBoundingClientRect();
                const linkRect = activeTocLink.getBoundingClientRect();
                const linkOffsetTop = (activeTocLink as HTMLElement).offsetTop;
                const containerHeight = tocContainer.clientHeight;
                const linkHeight = linkRect.height;

                // Calculate if the link is visible in the TOC container
                const isLinkVisible = linkRect.top >= tocContainerRect.top &&
                    linkRect.bottom <= tocContainerRect.bottom;

                if (!isLinkVisible) {
                    // Calculate scroll position
                    const scrollTo = linkOffsetTop - (containerHeight / 2) + (linkHeight / 2);

                    // Use requestAnimationFrame for smoother scrolling
                    requestAnimationFrame(() => {
                        tocContainer.scrollTo({
                            top: Math.max(0, scrollTo),
                            behavior: 'smooth'
                        });
                    });
                }
            }
        };

        // Throttled scroll handler for better performance
        let ticking = false;
        const throttledHandleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        // Setup scroll listener
        const setupScrollListener = () => {
            window.addEventListener('scroll', throttledHandleScroll);
            // Initial check after content is loaded
            setTimeout(() => {
                handleScroll();
            }, 200);
        };

        // Wait for content to be fully loaded
        const checkContentLoaded = () => {
            const contentElement = document.querySelector('.blog-content');
            if (contentElement && contentElement.children.length > 0) {
                setupScrollListener();
                return true;
            }
            return false;
        };

        if (checkContentLoaded()) {
            return;
        }

        // If content not ready, wait for it
        const timer = setTimeout(() => {
            checkContentLoaded();
        }, 500);

        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
            clearTimeout(timer);
        };
    }, [blog.tableOfContents, activeTocId]);

    // Handle TOC container scroll events with debouncing
    useEffect(() => {
        let scrollTimeout: NodeJS.Timeout;

        const handleTocScroll = (e: Event) => {
            // Prevent TOC scroll from affecting main page scroll
            e.stopPropagation();
        };

        const debouncedHandleTocScroll = (e: Event) => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => handleTocScroll(e), 16); // ~60fps
        };

        const tocContainer = tocRef.current;
        if (tocContainer) {
            tocContainer.addEventListener('scroll', debouncedHandleTocScroll, { passive: false });
            return () => {
                tocContainer.removeEventListener('scroll', debouncedHandleTocScroll);
                clearTimeout(scrollTimeout);
            };
        }
    }, [blog.tableOfContents]);

    // Function to handle TOC link clicks with header offset
    const handleTocClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
        e.preventDefault();

        // Set active TOC item immediately when clicked
        setActiveTocId(targetId);

        // Set flag to indicate we're programmatically scrolling
        isProgrammaticScrollRef.current = true;
        programmaticScrollTargetRef.current = targetId;

        const scrollToElement = (element: HTMLElement) => {
            // Batch all DOM reads to prevent forced reflows
            const currentScrollY = window.scrollY;
            const elementRect = element.getBoundingClientRect();
            const elementTop = elementRect.top + currentScrollY;

            // Calculate offset considering fixed header
            const headerHeight = 100; // Increased header height for better spacing
            const additionalOffset = 20; // Extra spacing for better visual separation
            const targetPosition = elementTop - headerHeight - additionalOffset;

            // Ensure we don't scroll to negative position
            const finalPosition = Math.max(0, targetPosition);

            // Use requestAnimationFrame to ensure smooth scrolling without blocking
            requestAnimationFrame(() => {
                // Try to use Lenis if available, otherwise fallback to native scroll
                const scrollDuration = 1200; // 1.2 seconds in milliseconds

                if (typeof window !== 'undefined' && (window as unknown as { lenis?: { scrollTo: (position: number, options?: { duration: number; easing: (t: number) => number }) => void } }).lenis) {
                    (window as unknown as { lenis: { scrollTo: (position: number, options?: { duration: number; easing: (t: number) => number }) => void } }).lenis.scrollTo(finalPosition, {
                        duration: 1.2,
                        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
                    });

                    // Clear flag after scroll animation completes
                    setTimeout(() => {
                        isProgrammaticScrollRef.current = false;
                        programmaticScrollTargetRef.current = '';
                    }, scrollDuration);
                } else {
                    window.scrollTo({
                        top: finalPosition,
                        behavior: 'smooth'
                    });

                    // Clear flag after scroll animation completes (smooth scroll is typically ~500-1000ms)
                    setTimeout(() => {
                        isProgrammaticScrollRef.current = false;
                        programmaticScrollTargetRef.current = '';
                    }, 1500); // Give a bit more time for smooth scroll
                }
            });
        };

        // Function to find and scroll to element
        const findAndScroll = () => {
            // First try to find by ID
            const targetElement = document.getElementById(targetId);
            if (targetElement) {
                scrollToElement(targetElement);
                return true;
            }

            // If not found by ID, try to find by heading text content
            const allHeadings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
            const headingArray = Array.from(allHeadings);

            // Find heading by text content (case-insensitive, more flexible matching)
            const targetHeading = headingArray.find(heading => {
                if (!heading.textContent) return false;

                // Clean the heading text for comparison
                const headingText = heading.textContent
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .trim();

                return headingText === targetId.toLowerCase();
            });

            if (targetHeading) {
                scrollToElement(targetHeading as HTMLElement);
                return true;
            }

            return false;
        };

        // Try to find and scroll immediately
        if (!findAndScroll()) {
            // If not found immediately, wait for DOM to be ready and try again
            setTimeout(() => {
                if (!findAndScroll()) {
                    // Last resort: try with a longer delay for dynamic content
                    setTimeout(() => {
                        findAndScroll();
                    }, 300);
                }
            }, 100);
        }
    };

    // Function to render Table of Contents
    const renderTableOfContents = (toc: TableOfContentsItem[]) => {
        if (!toc || toc.length === 0) {
            return (
                <div className="toc-body">
                    <p>No table of contents available for this post.</p>
                </div>
            );
        }

        // Define types for TOC structure
        interface TocItem {
            id: string;
            text: string;
            level: number;
        }

        interface GroupedTocItem extends TocItem {
            children: TocItem[];
        }

        // Group items by level 2 and their level 3 children
        const groupedToc: GroupedTocItem[] = [];
        let currentLevel2Item: TocItem | null = null;
        let level3Items: TocItem[] = [];

        toc.forEach((item) => {
            if (item.level === 2) {
                // If we have a previous level 2 item, add it with its level 3 children
                if (currentLevel2Item) {
                    groupedToc.push({
                        ...currentLevel2Item,
                        children: [...level3Items]
                    });
                }
                // Start new level 2 item
                currentLevel2Item = item;
                level3Items = [];
            } else if (item.level === 3 && currentLevel2Item) {
                // Add level 3 item to current level 2
                level3Items.push(item);
            } else {
                // Handle level 1 or other levels as before
                groupedToc.push({ ...item, children: [] });
            }
        });

        // Add the last level 2 item if it exists
        if (currentLevel2Item) {
            const groupedItem: GroupedTocItem = {
                id: (currentLevel2Item as TocItem).id,
                text: (currentLevel2Item as TocItem).text,
                level: (currentLevel2Item as TocItem).level,
                children: [...level3Items]
            };
            groupedToc.push(groupedItem);
        }

        return (
            <div className="toc-body" ref={tocRef}>
                {groupedToc.map((item) => {
                    const isActive = activeTocId === item.id;

                    if (item.level === 2 && item.children && item.children.length > 0) {
                        // Render level 2 with nested level 3 items as ul/li
                        return (
                            <div key={item.id} className={`toc-item toc-level-${item.level}`}>
                                <a
                                    href={`#${item.id}`}
                                    className="toc-link"
                                    onClick={(e) => handleTocClick(e, item.id)}
                                    style={{
                                        display: 'block',
                                        padding: '8px 0',
                                        textDecoration: 'none',
                                        color: isActive ? '#333333' : '#787878',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        lineHeight: '24px',
                                        transition: 'color 0.3s ease'
                                    }}
                                >
                                    {item.text}
                                </a>
                                <ul style={{ marginLeft: '30px', marginTop: '8px', paddingLeft: '0' }}>
                                    {item.children.map((child: TocItem) => {
                                        const isChildActive = activeTocId === child.id;
                                        return (
                                            <li key={child.id} style={{ listStyle: 'disc' }}>
                                                <a
                                                    href={`#${child.id}`}
                                                    className="toc-link"
                                                    onClick={(e) => handleTocClick(e, child.id)}
                                                    style={{
                                                        textDecoration: 'none',
                                                        color: isChildActive ? '#333333' : '#787878',
                                                        fontSize: '16px',
                                                        fontWeight: isChildActive ? '500' : '400',
                                                        cursor: 'pointer',
                                                        lineHeight: '20px',
                                                        transition: 'color 0.3s ease'
                                                    }}
                                                >
                                                    {child.text}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    } else {
                        // Render other levels as before
                        return (
                            <div key={item.id} className={`toc-item toc-level-${item.level}`}>
                                <a
                                    href={`#${item.id}`}
                                    className="toc-link"
                                    onClick={(e) => handleTocClick(e, item.id)}
                                    style={{
                                        display: 'block',
                                        padding: '8px 0',
                                        textDecoration: 'none',
                                        color: isActive ? '#333333' : '#787878',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        lineHeight: '24px',
                                        transition: 'color 0.3s ease'
                                    }}
                                >
                                    {item.text}
                                </a>
                            </div>
                        );
                    }
                })}
            </div>
        );
    };



    return (
        <>
            <section className="blog-page-sec blog-detail-page section-padding-bottom">
                <div className="container custom-container">
                    <div className="row">
                        <div className="col-12">
                            <div className="blog-item-wrapper">
                                <div className="blog-item blog-item-details">
                                    <div className="row justify-content-center">
                                        <div className="col-xl-12">
                                            <div className="title-box blog-title">
                                                <h1>
                                                    {blog.title}
                                                </h1>
                                            </div>
                                            <div className="meta-box">
                                                <ul className="custom-ul meta-info d-flex">
                                                    <li>
                                                        <span>
                                                            {blog.date}
                                                        </span>
                                                    </li>
                                                    <li>
                                                        <span>
                                                            {blog.tags[0] || 'General'}
                                                        </span>
                                                    </li>
                                                    <li>
                                                        <span>
                                                            by {blog.author}
                                                        </span>
                                                    </li>
                                                    {blog.readingTime && (
                                                        <li>
                                                            <span>
                                                                {blog.readingTime}
                                                            </span>
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* <div className="img-box overflow-hidden">
                                        <Image
                                            height={0}
                                            width={0}
                                            sizes="100vw"
                                            src="/images/blog/blog-details-1.png"
                                            alt="blog"
                                            data-speed="0.8"
                                            className="w-100 d-block"
                                        />
                                    </div> */}

                                    <div className="content-box row-padding-top">
                                        <div
                                            ref={scrollRef}
                                            className="row justify-content-center position-relative"
                                        >
                                            <div className="col-xl-3 d-none d-xl-block ps-0">
                                                <div className="position-sticky sticky-col mr-40">
                                                    <aside className="toc-card-wrapper">
                                                        <div className="toc-card mb-4">
                                                            <div className="toc-header">
                                                                <h2 className="toc-title">Table of Contents</h2>
                                                            </div>
                                                            {renderTableOfContents(blog.tableOfContents || [])}
                                                        </div>
                                                    </aside>
                                                    <div className="social-links sticky-social">
                                                        <ul className="custom-ul">
                                                            {social.map((item, i) => (
                                                                <li key={i}>
                                                                    <Link
                                                                        href={item.href}
                                                                        target="_blank"
                                                                    >
                                                                        <i
                                                                            className={`${item.iconClass} fs-5`}
                                                                        />
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Blog Body render Here md file  */}
                                            <div className="col-xl-6 col-xxl-6">
                                                <CodeBlock />
                                                <div className="blog-body">
                                                    <div
                                                        className="blog-content"
                                                        dangerouslySetInnerHTML={{ __html: blog.contentHtml }}
                                                    />
                                                </div>
                                                <BlogCommentForm blogSlug={blog.slug} />
                                            </div>

                                            <div className="col-xl-3 d-none d-xl-block pe-0">
                                                <aside className="position-sticky sticky-col ml-40">
                                                    <div className="contact-card">
                                                        <div className="contact-card__head">
                                                            <h3 className="contact-title">Get in touch</h3>
                                                        </div>

                                                        {getInTouchStatus.type && (
                                                            <div className={`alert ${getInTouchStatus.type === 'success' ? 'alert-success' : 'alert-danger'} mb-3 fade-in`} style={{ transition: 'opacity 0.3s ease-out' }}>
                                                                {getInTouchStatus.message}
                                                            </div>
                                                        )}

                                                        <form className="get-in-touch_form" onSubmit={handleGetInTouchSubmit}>
                                                            <div className="row g-3">
                                                                <div className="col-12">
                                                                    <div className="mb-2">
                                                                        <input
                                                                            type="text"
                                                                            id="contact-name"
                                                                            name="name"
                                                                            className="form-control"
                                                                            placeholder="Your name"
                                                                            value={getInTouchData.name}
                                                                            onChange={handleGetInTouchInputChange}
                                                                            required
                                                                            disabled={isSubmittingGetInTouch}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-12">
                                                                    <div className="mb-2">
                                                                        <input
                                                                            type="email"
                                                                            id="contact-email"
                                                                            name="email"
                                                                            className="form-control"
                                                                            placeholder="Enter your e-mail address"
                                                                            value={getInTouchData.email}
                                                                            onChange={handleGetInTouchInputChange}
                                                                            required
                                                                            disabled={isSubmittingGetInTouch}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-12">
                                                                    <div className="mb-2">
                                                                        <textarea
                                                                            name="message"
                                                                            id="contact-message"
                                                                            className="form-control"
                                                                            rows={4}
                                                                            placeholder="Write your message..."
                                                                            value={getInTouchData.message}
                                                                            onChange={handleGetInTouchInputChange}
                                                                            required
                                                                            disabled={isSubmittingGetInTouch}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <p className='fs-6 lh-base'>By submitting this form I consent to the processing of my personal data.</p>
                                                                <div className="col-12 mt-4">
                                                                    <button
                                                                        type="submit"
                                                                        className="desilo-link-btn btn-pill mt-2 w-100 justify-content-center"
                                                                        disabled={isSubmittingGetInTouch}
                                                                    >
                                                                        {isSubmittingGetInTouch ? 'Sending...' : 'Get in touch'}
                                                                        <span>
                                                                            <i className="fa-solid fa-arrow-right arry1" />
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </form>
                                                    </div>
                                                </aside>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default BlogSingleInfo;
